<script lang="ts">
  /**
   * Reusable Modal Component
   *
   * Usage:
   * <Modal isOpen={showModal} title="My Modal" onClose={() => showModal = false}>
   *   <p>Modal content here</p>
   *   {#snippet actions()}
   *     <button class="btn-secondary" onclick={() => showModal = false}>Cancel</button>
   *     <button class="btn-primary" onclick={handleSubmit}>Submit</button>
   *   {/snippet}
   * </Modal>
   */

  import type { Snippet } from 'svelte';

  type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

  interface Props {
    isOpen: boolean;
    title: string;
    size?: ModalSize;
    onClose: () => void;
    children?: Snippet;
    actions?: Snippet;
  }

  let { isOpen, title, size = 'md', onClose, children, actions }: Props = $props();

  function handleOverlayClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (isOpen && e.key === 'Escape') {
      onClose();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

{#if isOpen}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-overlay" onclick={handleOverlayClick}>
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="modal"
      class:modal-sm={size === 'sm'}
      class:modal-lg={size === 'lg'}
      class:modal-xl={size === 'xl'}
      onclick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div class="modal-header">
        <h3 id="modal-title">{title}</h3>
        <button class="btn-close" onclick={onClose} aria-label="Close modal">&times;</button>
      </div>

      <div class="modal-body">
        {#if children}
          {@render children()}
        {/if}
      </div>

      {#if actions}
        <div class="modal-actions">
          {@render actions()}
        </div>
      {/if}
    </div>
  </div>
{/if}
